<script setup lang="ts">
import { ref } from 'vue';
import { TrashIcon } from '@heroicons/vue/24/outline';
import { useToast } from 'vue-toastification';

import useModal from '@/modules/shared/composable/useModal';
import ButtonsModal from '@/modules/shared/components/ButtonsModal.vue';
import useMailboxes from '@/modules/settings/composables/useMailboxes';
import useUsers from '@/modules/settings/composables/useUsers';
import useTeams from '@/modules/settings/composables/useTeams';
import { numberToWords } from '@/modules/settings/utils/helpers/numbers';
import useAlerts from '@/modules/explore/composables/useAlerts';
import ToastComponent from '@/modules/shared/components/ToastComponent.vue';
import { CheckCircleIcon } from '@heroicons/vue/20/solid';

const { deleteMailboxes, getMailboxes } = useMailboxes();
const { deleteUsers, getUsers } = useUsers();
const { deleteTeams, getTeams } = useTeams();
const { deleteAlerts, getAlerts } = useAlerts();
const { deleteData, hideModal } = useModal();
const toast = useToast();

const isDisabled = ref<boolean>(false);

const deleteFromBq = ref(false);

const remove = () => {
    isDisabled.value = true;
    if (deleteData.value.type === 'mailboxes') {
        deleteMailboxes({ data: { emails: deleteData.value.data, delete_from_bq: deleteFromBq.value } })
            .then(async () => {
                await getMailboxes();
                getUsers();
                getTeams();
                hideModal();
                const n = deleteData.value.data.length;
                toast.success(
                    {
                        component: ToastComponent,
                        props: {
                            title: `Mailbox${n > 1 ? 'es' : ''} removed`,
                            messenges: 'This change will be reflected in your dashboard tomorrow at 00:01 local time'
                        },
                        options: { icon: CheckCircleIcon }
                    },
                    {
                        timeout: 10000
                    }
                );
            })
            .catch(() => {
                toast.error('Something went wrong');
            });
    }
    if (deleteData.value.type === 'users') {
        deleteUsers({ data: { emails: deleteData.value.data } })
            .then(async () => {
                await getUsers();
                hideModal();
                const n = deleteData.value.data.length;
                toast.success(`${numberToWords(n)} user${n > 1 ? 's' : ''} removed`);
            })
            .catch(() => {
                toast.error('Something went wrong');
            });
    }
    if (deleteData.value.type === 'teams') {
        deleteTeams({ data: { teams: deleteData.value.data } })
            .then(async () => {
                await getTeams();
                getUsers();
                hideModal();
                toast.success(
                    {
                        component: ToastComponent,
                        props: {
                            title: `Team${deleteData.value.data.length > 1 ? 's' : ''} removed`,
                            messenges: 'This change will be reflected in your dashboard tomorrow at 00:01 local time'
                        },
                        options: { icon: CheckCircleIcon }
                    },
                    {
                        timeout: 10000
                    }
                );
            })
            .catch(() => {
                toast.error('Something went wrong');
            });
    }
    if (deleteData.value.type === 'alerts') {
        deleteAlerts({ data: { alerts: deleteData.value.data } })
            .then(async () => {
                await getAlerts();
                hideModal();
                const n = deleteData.value.data.length;
                toast.success(`Your alert${n > 1 ? 's' : ''} ${n > 1 ? 'have' : 'has'} been removed`);
            })
            .catch(() => {
                toast.error('Something went wrong');
            });
    }
    isDisabled.value = true;
};
</script>

<template>
    <div class="p-6 pb-0 overflow-auto">
        <div class="bg-red-100 w-12 h-12 flex items-center justify-center rounded-full">
            <TrashIcon class="w-6 h-6 text-red-500" />
        </div>

        <p class="text-gray-900 font-semibold leading-8 text-lg mt-5">{{ deleteData.title }}</p>
        <p class="text-sm leading-5 font-normal text-gray-500 pt-2">{{ deleteData.text }}</p>

        <div v-if="deleteData.type === 'mailboxes'" class="pt-5">
            <fieldset>
                <div class="relative flex items-start">
                    <div class="flex h-6 items-center">
                        <input
                            id="remove"
                            aria-describedby="remove-description"
                            v-model="deleteFromBq"
                            name="remove"
                            type="checkbox"
                            class="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-0 focus:ring-offset-0"
                        />
                        <p class="ml-2 text-sm leading-5 font-medium text-gray-700">Delete all their existing email data from the dashboard</p>
                    </div>
                </div>
            </fieldset>
        </div>
    </div>

    <ButtonsModal type="Remove" color="red" :isDisabled="isDisabled" @submit="remove" />
</template>

<style scoped></style>
